import { useState, useCallback, useEffect } from 'react';
import {
	getUserProperty,
	setUserProperties,
} from '@atlassian/jira-common-rest/src/api/latest/user-properties';
import log from '@atlassian/jira-common-util-logging/src/log';
import { type AccountId, toBaseUrl } from '@atlassian/jira-shared-types';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller';

const getHasSeenOnboarding = (
	accountId: AccountId | null,
	onboardingKey: string,
): Promise<boolean> =>
	// @ts-expect-error - TS2322 - Type 'Promise<unknown>' is not assignable to type 'Promise<boolean>'.
	accountId
		? getUserProperty(toBaseUrl(''), accountId, onboardingKey).catch((error) => {
				/**
				 * If we can't find key, we know they have never seen the new onboarding modal before. So return false
				 */
				if (error.statusCode === 404) {
					return false;
				}

				log.safeErrorWithoutCustomerData(
					'getHasSeenOnboarding',
					`Error reading data from getHasSeenOnboarding with property key ${onboardingKey}`,
					error,
				);
				// Fall back to not showing the onboarding modal in case of errors
				return true;
			})
		: new Promise((resolve: (result: Promise<boolean> | boolean) => void) => {
				log.safeErrorWithoutCustomerData(
					'getHasSeenOnboarding',
					`Failed to get getHasSeenOnboarding with property key ${onboardingKey} - no accountId`,
				);
				resolve(true);
			});

const setHasSeenOnboarding = (
	accountId: AccountId | null,
	onboardingKey: string,
): Promise<boolean | undefined> =>
	accountId
		? setUserProperties(toBaseUrl(''), accountId, onboardingKey, String(true)).catch((error) => {
				log.safeErrorWithoutCustomerData(
					'setHasSeenOnboarding',
					`Failed to update boolean value in setHasSeenOnboarding with property key ${onboardingKey}`,
					error,
				);
			})
		: new Promise(() => {
				log.safeErrorWithoutCustomerData(
					'setHasSeenOnboarding',
					`Failed to update boolean value in setHasSeenOnboarding with property key ${onboardingKey} - no accountId`,
				);
			});

export const useOnboardingState: (arg1: string) => {
	shouldShowOnboarding: boolean;
	stopOnboarding: () => void;
} = (onboardingKey) => {
	const { atlassianAccountId } = useTenantContext();
	const [shouldShowOnboarding, setShouldShowOnboarding] = useState<boolean>(false);

	const fetchOnboardingState = useCallback(async () => {
		const hasSeenOnboarding = await getHasSeenOnboarding(atlassianAccountId, onboardingKey);
		setShouldShowOnboarding(!hasSeenOnboarding);
	}, [atlassianAccountId, onboardingKey]);

	const stopOnboarding = useCallback(() => {
		setHasSeenOnboarding(atlassianAccountId, onboardingKey);
	}, [atlassianAccountId, onboardingKey]);

	useEffect(() => {
		fetchOnboardingState();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return { shouldShowOnboarding, stopOnboarding };
};

export const useStopOnboarding: (arg1: string) => void = (onboardingKey) => {
	const { atlassianAccountId } = useTenantContext();
	useEffect(() => {
		setHasSeenOnboarding(atlassianAccountId, onboardingKey);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);
};
